<script lang="ts">
  import Tabs from '../components/Tabs.svelte';
  import { hasCommandModifier, isEditableTarget } from '../components/keyboard';
  import StackLab from '../components/labs/StackLab.svelte';
  import QueueLab from '../components/labs/QueueLab.svelte';
  import DictLab from '../components/labs/DictLab.svelte';
  import { intro } from '../components/labs/lab-session.svelte';
  import { catalog } from '../services';
  import {
    formatRoute,
    labKindTitle,
    labStepTitle,
    LAB_KINDS,
    type LabKind,
    type LabStep,
  } from '../lib/router';

  /**
   * ラボの枠（layout-and-navigation.md §5）。中身は StackLab / QueueLab / DictLab（components.md §3.2）。
   * 見出しは構造の名前（スタック（LIFO）など）。手順 0「困る場面」では、まず素朴な方法で解いて
   * 間違う・遅いことを確かめてから盤面を出す（docs/curriculum/labs.md）。比喩は使わない。
   */
  interface Props {
    kind: LabKind | undefined;
    step: LabStep;
    onnavigate: (hash: string, replace?: boolean) => void;
  }

  let { kind, step, onnavigate }: Props = $props();

  const SUBTITLES: Record<LabKind, string> = {
    stack: '最後に入れた要素から取り出す。append と popLast',
    queue: '先に入れた要素から取り出す。append と、先頭の添字 head',
    dict: 'キーで値を引く辞書と、要素があるかを調べる Set',
  };
  const TILES: Record<LabKind, string> = {
    stack:
      '括弧の対応判定。カウンタだけでは ([)] を見分けられないので、開き括弧をスタックに積んで閉じ括弧と照合します',
    queue:
      '来た順に処理する。removeFirst() は残りを毎回詰め直す O(N) なので、先頭の添字 head を進めて O(1) にします',
    dict: 'キーが 10^9 までの番号や文字列だと配列の添字にできないので、辞書で値を引き、Set で出現済みかを調べます',
  };
  /** 各ラボの冒頭に出す使いどころ（content/use-cases.yaml の章の値）。 */
  const TEXTBOOK: Record<LabKind, string> = {
    stack: '/algo/06-stack-queue/',
    queue: '/algo/06-stack-queue/',
    dict: '/kyopro/03-dict-set/',
  };
  const STEP_KEYS: Record<string, LabStep> = { '1': 1, '2': 2, '3': 3 };

  const kindItems = LAB_KINDS.map((k) => ({ id: k, label: labKindTitle(k) }));
  const stepItems: { id: LabStep; label: string; n: number }[] = ([1, 2, 3] as const).map((s) => ({
    id: s,
    label: labStepTitle(s),
    n: s,
  }));
  const useCase = $derived(kind ? (catalog.useCases?.textbooks[TEXTBOOK[kind]] ?? null) : null);
  const ready = $derived(kind ? intro[kind] : false);

  function go(k: LabKind, s: LabStep, replace = false) {
    onnavigate(formatRoute({ name: 'lab', kind: k, step: s }), replace);
  }

  function onkeydown(e: KeyboardEvent) {
    if (!kind || e.defaultPrevented || hasCommandModifier(e) || isEditableTarget(e.target)) return;
    const s = STEP_KEYS[e.key];
    if (s !== undefined) {
      e.preventDefault();
      go(kind, s, true);
    }
  }
</script>

<svelte:window {onkeydown} />

<div class="page lab-page">
  <div class="page-head">
    <h1 tabindex="-1">{kind ? labKindTitle(kind) : 'ラボ'}</h1>
    {#if kind}<span class="text-soft small">{SUBTITLES[kind]}</span>{/if}
  </div>
  {#if kind && ready && useCase}
    <p class="use-case small"><span class="text-soft">使いどころ:</span> {useCase}</p>
  {/if}

  <Tabs
    label="ラボの種類"
    items={kindItems}
    selected={kind}
    panelId="lab-kind-panel"
    idPrefix="lab-kind"
    onselect={(k) => go(k, 1)}
  />

  <div id="lab-kind-panel" role="tabpanel" aria-labelledby={kind ? `lab-kind-${kind}` : undefined}>
    {#if kind}
      <div class="steps">
        <Tabs
          label="段階"
          items={stepItems}
          selected={step}
          panelId="lab-step-panel"
          idPrefix="lab-step"
          onselect={(s) => go(kind, s, true)}
        />
      </div>
      <div
        id="lab-step-panel"
        role="tabpanel"
        aria-labelledby="lab-step-{step}"
        data-testid="lab-panel"
      >
        {#if !ready && step !== 1}
          <div class="panel gate">
            <p>このラボは、段階 1 の最初の場面から始めます。</p>
            <button type="button" class="btn btn-primary" onclick={() => go(kind, 1, true)}
              >段階 1 へ</button
            >
          </div>
        {:else if kind === 'stack'}
          <StackLab {step} ongo={(s) => go('stack', s, true)} />
        {:else if kind === 'queue'}
          <QueueLab {step} ongo={(s) => go('queue', s, true)} />
        {:else}
          <DictLab {step} ongo={(s) => go('dict', s, true)} />
        {/if}
      </div>
    {:else}
      <div class="tiles">
        {#each LAB_KINDS as k (k)}
          <a class="tile" href={formatRoute({ name: 'lab', kind: k, step: 1 })}>
            <strong>{labKindTitle(k)}</strong><span>{TILES[k]}</span>
          </a>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .page-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-4);
    flex-wrap: wrap;
    margin-bottom: var(--space-2);
  }
  .use-case {
    margin: 0 0 var(--space-4);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .steps {
    margin: var(--space-4) 0 var(--space-5);
  }
  .gate {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-start;
  }
  .gate p {
    margin: 0;
  }
  .tiles {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--space-3);
    margin-top: var(--space-5);
  }
  .tile {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--text);
  }
  .tile:hover {
    text-decoration: none;
    background: var(--canvas);
  }
  .tile span {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }

  /* ---- ラボの共通部品（components/labs/*.svelte が使う。mockups/lab-stack.html と同じ見た目） ---- */
  .lab-page :global(.lab) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .lab-page :global(.lab > .panel h2) {
    margin: 0;
  }
  .lab-page :global(.lab > .board),
  .lab-page :global(.lab > .log) {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .lab-page :global(.lab > .board) {
    min-height: 22rem;
  }
  .lab-page :global(.controls) {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
    align-items: flex-end;
  }
  .lab-page :global(.controls .value) {
    width: 5rem;
  }
  .lab-page :global(.controls .value input) {
    font-family: var(--font-mono);
    text-align: center;
  }
  .lab-page :global(.field label) {
    display: block;
    font-size: var(--fs-sm);
    margin-bottom: var(--space-1);
  }
  .lab-page :global(.status-line) {
    min-height: 1.6em;
    margin: 0;
    font-size: var(--fs-sm);
    color: var(--text);
  }
  .lab-page :global(.counters) {
    display: flex;
    gap: var(--space-5);
    flex-wrap: wrap;
    margin: 0;
  }
  .lab-page :global(.counters div) {
    display: flex;
    flex-direction: column;
  }
  .lab-page :global(.counters dt) {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .lab-page :global(.counters dd) {
    margin: 0;
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
    font-variant-numeric: tabular-nums;
  }
  .lab-page :global(.small) {
    font-size: var(--fs-sm);
  }
  @media (max-width: 1023px) {
    .lab-page :global(.lab) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  @media (max-width: 639px) {
    .tiles {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
